import React, { useState } from "react"
import {MessageSquarePlus, X, Send} from 'lucide-react'
import { lumi } from "../lib/lumi"

interface ReviewerModeProps {
  section: string
  contentKey: string
  currentValue: string
  elementType: "text" | "button"
}

export function ReviewerMode({ section, contentKey, currentValue, elementType }: ReviewerModeProps) {
  const [showFeedback, setShowFeedback] = useState(false)
  const [feedback, setFeedback] = useState("")
  const [sending, setSending] = useState(false)

  const user = lumi.auth.user
  const isReviewer = user && (user.userRole === "USER" || user.userRole === "ADMIN")

  const handleSubmitFeedback = async () => {
    if (!feedback.trim() || !user) return

    setSending(true)
    try {
      await lumi.entities.tasks.create({
        elementType,
        section,
        contentKey,
        currentValue,
        feedback: feedback.trim(),
        status: "pending",
        createdBy: user.userId,
        createdByName: user.userName,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      })

      setFeedback("")
      setShowFeedback(false)
      alert("✅ Feedback envoyé avec succès !")
    } catch (error) {
      console.error("Failed to submit feedback:", error)
      alert("❌ Échec de l'envoi. Veuillez réessayer.")
    } finally {
      setSending(false)
    }
  }

  if (!isReviewer) return null

  return (
    <div className="relative inline-block">
      {!showFeedback ? (
        <button
          onClick={() => setShowFeedback(true)}
          className="opacity-0 group-hover:opacity-100 transition-opacity bg-cyan-600 text-white p-2 rounded-lg hover:bg-cyan-700 shadow-lg"
          title="Ajouter un feedback"
        >
          <MessageSquarePlus className="w-4 h-4" />
        </button>
      ) : (
        <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-80 bg-white rounded-xl shadow-2xl border-2 border-cyan-600 p-4 z-[9999]">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-semibold text-gray-900 flex items-center gap-2">
              <MessageSquarePlus className="w-5 h-5 text-cyan-600" />
              Ajouter un feedback
            </h4>
            <button
              onClick={() => setShowFeedback(false)}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="mb-3">
            <div className="text-xs text-gray-500 mb-1">Élément actuel :</div>
            <div className="text-sm bg-gray-100 p-2 rounded border border-gray-200 max-h-20 overflow-y-auto">
              {currentValue}
            </div>
          </div>

          <textarea
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            placeholder="Décrivez les modifications à apporter..."
            className="w-full border-2 border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-600 focus:border-transparent min-h-[100px] mb-3"
            autoFocus
          />

          <button
            onClick={handleSubmitFeedback}
            disabled={!feedback.trim() || sending}
            className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-cyan-600 to-teal-600 text-white px-4 py-2 rounded-lg hover:shadow-lg hover:scale-105 transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            <Send className="w-4 h-4" />
            {sending ? "Envoi..." : "Envoyer le feedback"}
          </button>
        </div>
      )}
    </div>
  )
}